import React from 'react'
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { Button } from '../ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Separator } from '../ui/separator'
import { Badge } from '../ui/badge'
import { UserSearchAutocomplete } from '../ui/UserSearchAutocomplete'
import { useDiagramLinks, type Role, type DiagramAccessLink } from '@/hooks/useDiagramLinks'
import { useDiagramAccess } from '@/hooks/useDiagramAccess'
import { useDiagramCollaborators } from '@/hooks/useDiagramCollaborators'
import { useDiagramAccessPolicy } from '@/hooks/useDiagramAccessPolicy'
import { useDiagramInfo } from '@/hooks/useDiagramInfo'
import { useQuery } from '@tanstack/react-query'
import { useEntitlements } from '@/hooks/useEntitlements'
import { getGravityUserId, useAuthenticatedFetch } from '@/hooks/useAuth'
import { TypesenseUser } from '@/lib/typesense/types'
import { TypesenseService } from '@/lib/typesense/TypesenseService'
import { Users, Link, Trash2, Shield, UserPlus, X, Share2, Check, Eye, RefreshCw, Loader2 } from 'lucide-react'
import { useUpgradeModalStore } from '../../stores/upgradeModalStore'
import { useTranslation } from '@/hooks/useTranslation'
import i18n from '@/i18n/config'
import { Logger } from '@/utils/logger'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import { Tracker } from '../../domains/analytics/services/Tracker'
import { RETENTION, VALUE_PREDICTORS } from '../../domains/analytics/constants/MetricEvents'
import type { FieldVisibilityConfig } from '../../domains/viewer/utils/fieldVisibility'
import { FOLDER_RECORD_PROJECTION_POLICY_VERSION } from '../../core/fields/security/folderRecordProtection'
import { PUBLIC_VIEWER_FIELD_VISIBILITY } from '../../domains/viewer/utils/publicViewerPolicy'
import { useDiagramDocument } from '@/contexts/DiagramDocumentContext'
import {
  assertNonEmptyOrgViewerPayload,
  buildOrgChartViewerPayload,
  buildGenogramViewerPayload,
  EmptyViewerSnapshotError,
} from '../../domains/viewer/utils/buildViewerSnapshot'
import { useDiagramStore } from '@/stores/diagramStore'
import { OrganizationAccessControl } from './OrganizationAccessControl'
import { toast } from 'sonner'

const logger = Logger.getLoggerWithPrefix('ShareDialog')

async function readPublishErrorMessage(response: Response, fallback: string): Promise<string> {
  const text = await response.text().catch(() => '')
  if (!text) return fallback
  try {
    const body = JSON.parse(text) as { error?: unknown; code?: unknown }
    if (typeof body.error === 'string' && body.error.trim()) {
      if (body.code === 'ENTITLEMENTS_NOT_RESOLVED') {
        return 'We could not verify publishing access. Please try again.'
      }
      return body.error.trim()
    }
  } catch {
    // Fall through to plain text for non-JSON responses.
  }
  return text
}

interface ShareDialogProps {
  open: boolean
  onOpenChange: (v: boolean) => void
  diagramId?: string
}

export const ShareDialog: React.FC<ShareDialogProps> = ({ open, onOpenChange, diagramId }) => {
  const { t } = useTranslation('diagram')
  const currentUserId = React.useMemo(() => getGravityUserId(), [])
  const dialogTitleRef = React.useRef<HTMLHeadingElement>(null)

  // Access check: only moderator+ can manage collaborators/links
  const { data: access } = useDiagramAccess(diagramId)
  const canManage = (access?.role === 'moderator' || access?.role === 'owner')
  const { data: diagramInfo } = useDiagramInfo(diagramId)
  const isPublishableDiagram = diagramInfo?.type === 'org' || diagramInfo?.type === 'genogram'
  const isOrgChartDiagram = diagramInfo?.type === 'org'
  const hasViewerSupport = ['org', 'flowchart', 'genogram', 'network'].includes(diagramInfo?.type ?? '')
  const viewerDomain = diagramInfo?.type === 'org' ? 'orgchart'
    : diagramInfo?.type === 'flowchart' ? 'process'
    : diagramInfo?.type === 'genogram' ? 'genogram'
    : diagramInfo?.type === 'network' ? 'network'
    : null
  const viewerRoutePrefix = viewerDomain === 'orgchart' ? 'ov'
    : viewerDomain === 'process' ? 'pv'
    : viewerDomain === 'genogram' ? 'gv'
    : viewerDomain === 'network' ? 'nv'
    : null
  const authenticatedFetch = useAuthenticatedFetch()
  const { doc: diagramDoc } = useDiagramDocument()

  // Plan capabilities check
  const { canInviteByEmail, loading: entitlementsLoading } = useEntitlements()
  const { openModal: openUpgradeStoreModal } = useUpgradeModalStore()

  // User search and selection
  const [selectedUsers, setSelectedUsers] = React.useState<TypesenseUser[]>([])
  const [role, setRole] = React.useState<Role>('viewer')
  const idForQueries = canManage ? diagramId : undefined
  const { policy: accessPolicy, updatePolicy } = useDiagramAccessPolicy(idForQueries)
  const { links, createLink, deleteLink } = useDiagramLinks(idForQueries)
  const { collaborators, invite, update, remove } = useDiagramCollaborators(idForQueries)
  const upgradeTimerRef = React.useRef<number | null>(null)

  const [pendingVisibilityScroll, setPendingVisibilityScroll] = React.useState(false)
  const [isCreatingLink, setIsCreatingLink] = React.useState(false)
  const accessLinksBottomRef = React.useRef<HTMLDivElement | null>(null)

  const buildFieldVisibilityBody = React.useCallback(
    (): FieldVisibilityConfig => PUBLIC_VIEWER_FIELD_VISIBILITY,
    [],
  )

  // Pre-initialize Typesense when dialog opens
  React.useEffect(() => {
    if (open && canManage) {
      const typesenseService = new TypesenseService();
      typesenseService.preInitialize();
    }
  }, [open, canManage]);

  React.useEffect(() => () => {
    if (upgradeTimerRef.current !== null) {
      window.clearTimeout(upgradeTimerRef.current)
    }
  }, [])

  const openUpgradeModal = React.useCallback(() => {
    onOpenChange(false)
    if (upgradeTimerRef.current !== null) {
      window.clearTimeout(upgradeTimerRef.current)
    }
    upgradeTimerRef.current = window.setTimeout(() => {
      openUpgradeStoreModal('sharing.invite_by_email', t('share.upgradeMessage'))
      upgradeTimerRef.current = null
    }, 50)
  }, [onOpenChange, openUpgradeStoreModal, t])

  const handleUserSelect = (user: TypesenseUser) => {
    setSelectedUsers(prev => {
      if (prev.some(existing => existing.email.toLowerCase() === user.email.toLowerCase())) {
        return prev;
      }
      return [...prev, user];
    });
  };

  const handleUserRemove = (userId: string) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== userId));
  };

  const hasValidUsers = selectedUsers.length > 0

  const handleShareWithPeople = async () => {
    if (!diagramId || !hasValidUsers || invite.isPending) return

    if (!canInviteByEmail()) {
      openUpgradeModal()
      return
    }

    const emails = selectedUsers.map(user => user.email)
    if (emails.length === 0) return

    try {
      await invite.mutateAsync({ emails, role })
      setSelectedUsers([])
      Tracker.track(VALUE_PREDICTORS.COLLABORATION_STARTED, {
        method: 'email_invite',
        count: emails.length,
        role,
      })
      toast.success(t('share.accessShared', { count: emails.length }))
    } catch (error) {
      logger.error('Failed to share diagram access', error)
      toast.error(t('share.accessShareError'))
    }
  }

  const accessLinks = React.useMemo(
    () => links.data?.filter(link => link.role !== 'owner') ?? [],
    [links.data],
  )
  const hasPublishedFieldVisibility = canManage && isOrgChartDiagram && accessLinks.some(link => link.role === 'viewer')

  const openPublishedFieldVisibilityAndScroll = React.useCallback(() => {
    setPendingVisibilityScroll(true)
  }, [])

  React.useEffect(() => {
    if (!pendingVisibilityScroll || !accessLinksBottomRef.current) {
      return
    }

    const frameId = window.requestAnimationFrame(() => {
      accessLinksBottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
      setPendingVisibilityScroll(false)
    })

    return () => window.cancelAnimationFrame(frameId)
  }, [pendingVisibilityScroll])

  return (
    <Dialog open={open} onOpenChange={onOpenChange} >
      <TooltipProvider delayDuration={150}>
        <DialogContent
          data-event-load="share.dialog.load"
          className="sm:max-w-2xl max-h-[90vh] overflow-y-auto p-0 z-[1001]"
          overlayClassName="z-[1000]"
          showCloseButton={false}
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            dialogTitleRef.current?.focus()
          }}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <DialogClose asChild>
                <button
                  type="button"
                  data-testid="tid.dialog.share.close"
                  className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
                  aria-label={t('share.closeTooltip')}
                >
                  <X />
                </button>
              </DialogClose>
            </TooltipTrigger>
            <TooltipContent side="left" align="end" sideOffset={8}>
              {t('share.closeTooltip')}
            </TooltipContent>
          </Tooltip>
          <div className="py-4 px-6 space-y-4">
          <DialogHeader className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-black">
                <Share2 className="h-4 w-4" />
              </div>
              <DialogTitle className="text-lg font-semibold">
                <span tabIndex={-1} ref={dialogTitleRef}>
                {t('share.shareThisCanvas')}
                </span>
              </DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-6">
          {/* Invite collaborators section */}
          <div className="space-y-4">
            <div className="space-y-3">
              {!canInviteByEmail() && (
                <div className="flex justify-between space-x-4 rounded-lg border border-border/80 bg-primary/10 px-3 py-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2 font-light">
                    <Shield className="h-8 w-8" />
                    <span>{t('share.upgradeMessage')}</span>
                  </div>
                  <div>
                    <Button
                      variant="default"
                      data-testid="tid.dialog.share.upgradeNow"
                      onClick={openUpgradeModal}
                      className="h-9 px-4 text-sm font-medium"
                    >
                      {t('share.upgradeNow')}
                    </Button>
                  </div>
                </div>
              )}
              
              <UserSearchAutocomplete
                testId="tid.dialog.share.userSearch"
                onUserSelect={handleUserSelect}
                placeholder={!canInviteByEmail() ? t('share.upgradePlaceholder') : t('share.searchPlaceholder')}
                disabled={!canManage || !canInviteByEmail() || invite.isPending}
                className="w-full"
                inputClassName="h-[40px] rounded-lg border border-border/60 bg-background"
              />
              
              {/* Selected users display */}
              {selectedUsers.length > 0 && (
                <div className="flex flex-col gap-3 rounded-lg border border-primary/20 bg-primary/[0.03] p-2 sm:flex-row sm:items-center">
                  <div className="flex min-w-0 flex-1 flex-wrap gap-2">
                    {selectedUsers.map(user => (
                      <Badge
                        key={user.id}
                        variant="secondary"
                        className="inline-flex items-center gap-1"
                      >
                        {user.first_name && user.last_name
                          ? `${user.first_name} ${user.last_name}`
                          : user.email}
                        <button
                          data-testid={`tid.dialog.share.removeUser.${user.id}`}
                          className="opacity-70 hover:opacity-100 ml-1"
                          onClick={() => handleUserRemove(user.id)}
                          disabled={invite.isPending}
                          aria-label={`Remove ${user.first_name && user.last_name
                            ? `${user.first_name} ${user.last_name}`
                            : user.email}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Select value={role} onValueChange={(value: Role) => setRole(value)} disabled={!canManage || !canInviteByEmail() || invite.isPending}>
                      <SelectTrigger data-testid="tid.dialog.share.inviteRole" className="w-32 !h-10 rounded-lg border border-border/60">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem data-testid="tid.dialog.share.inviteRole.viewer" value="viewer">{t('share.viewer')}</SelectItem>
                        <SelectItem data-testid="tid.dialog.share.inviteRole.editor" value="editor">{t('share.editor')}</SelectItem>
                        <SelectItem data-testid="tid.dialog.share.inviteRole.moderator" value="moderator">{t('share.moderator')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      data-testid="tid.dialog.share.invite"
                      disabled={!canManage || entitlementsLoading || invite.isPending}
                      onClick={handleShareWithPeople}
                      className="h-[40px] shrink-0 rounded-lg px-5"
                    >
                      {invite.isPending
                        ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        : <UserPlus className="mr-2 h-4 w-4" />}
                      {invite.isPending
                        ? t('share.sharing')
                        : t('share.shareWithCount', { count: selectedUsers.length })}
                    </Button>
                  </div>
                </div>
              )}
              
              {!canManage && (
                <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-md">
                  <Shield className="h-4 w-4" />
                  <span>{t('share.onlyModeratorsInvite')}</span>
                </div>
              )}
            </div>
          </div>

          {/* People with access section */}
          {(!canManage || collaborators.isLoading || (collaborators.data?.length ?? 0) > 0) && <div className="space-y-4">
            <div className="flex items-center gap-2 text-md font-medium text-foreground">
              {/* <Users className="h-4 w-4 text-muted-foreground" /> */}
              <h3>{t('share.canvasAccess')}</h3>
            </div>

            <div className="space-y-3">
              {canManage && collaborators.isLoading && (
                <div className="text-sm text-muted-foreground text-left py-4">{t('share.loadingCollaborators')}</div>
              )}
              
              {canManage && collaborators.data && collaborators.data.length > 0 && (
                <div className="space-y-2">
                  {collaborators.data.map(c => {
                    const isCurrentUser = c.id === currentUserId
                    return <div key={`${c.kind}:${c.id}`} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <Users className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium truncate">{isCurrentUser ? t('share.you') : c.name || c.email || c.id}</div>
                            {c.role !== 'owner' && <span className="text-[10px] rounded bg-muted px-1.5 py-0.5 shrink-0">
                              {c.kind === 'guest' ? t('share.collaboratorKindGuest') : t('share.collaboratorKindUser')}
                            </span>}
                          </div>
                          {c.email && (isCurrentUser || c.name) && (
                            <div className="text-xs text-muted-foreground truncate">{c.email}</div>
                          )}
                        </div>
                      </div>
                      {c.role === 'owner' ? (
                        <span className="px-2 text-sm font-medium text-muted-foreground">{t('share.owner')}</span>
                      ) : <div className="flex items-center gap-2">
                        <Select 
                          value={c.role} 
                          onValueChange={(value: Role) => update.mutate({ collaboratorId: c.id, role: value })}
                          disabled={!canManage}
                        >
                          <SelectTrigger data-testid={`tid.dialog.share.collaboratorRole.${c.id}`} className="w-30 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem data-testid="tid.dialog.share.collaboratorRoleOption.viewer" value="viewer">{t('share.viewer')}</SelectItem>
                            <SelectItem data-testid="tid.dialog.share.collaboratorRoleOption.editor" value="editor">{t('share.editor')}</SelectItem>
                            <SelectItem data-testid="tid.dialog.share.collaboratorRoleOption.moderator" value="moderator">{t('share.moderator')}</SelectItem>
                          </SelectContent>
                        </Select>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              data-testid={`tid.dialog.share.removeCollaborator.${c.id}`}
                              onClick={() => remove.mutate(c.id)}
                              className="text-destructive hover:bg-red-400 hover:text-white"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent sideOffset={8}>
                            {t('share.deleteCollaboratorTooltip')}
                          </TooltipContent>
                        </Tooltip>
                      </div>}
                    </div>
                  })}
                </div>
              )}
              
              {!canManage && (
                <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-md">
                  <Shield className="h-4 w-4" />
                  <span>{t('share.onlyModeratorsView')}</span>
                </div>
              )}
            </div>
          </div>}

          <Separator className="bg-border/70" />

          {/* General access section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-md font-medium text-foreground">
              <h3>{t('share.generalAccess')}</h3>
            </div>

            <OrganizationAccessControl
              policy={accessPolicy.data}
              disabled={!canManage || accessPolicy.isLoading}
              saving={updatePolicy.isPending}
              error={accessPolicy.isError || updatePolicy.isError}
              onUpdate={(patch) => updatePolicy.mutate(patch)}
              t={t}
            />

            <CreateLinkRow
              onCreate={async (r, s) => {
                if (entitlementsLoading) return

                if (!canInviteByEmail()) {
                  openUpgradeModal()
                  return
                }

                if ((hasViewerSupport || isOrgChartDiagram) && (r === 'viewer' || hasPublishedFieldVisibility)) {
                  openPublishedFieldVisibilityAndScroll()
                }
                setIsCreatingLink(true)
                try {
                  await createLink.mutateAsync({ role: r, scope: s, expiresAt: null })
                  await links.refetch()
                } finally {
                  setIsCreatingLink(false)
                }
              }}
              canManage={canManage}
              entitlementsLoading={entitlementsLoading}
              isCreating={isCreatingLink}
              existingRoles={accessLinks.map(link => link.role)}
            />

            {!canManage && (
              <div className="flex items-center gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-600 dark:bg-amber-900/20 dark:text-amber-400">
                <Shield className="h-4 w-4" />
                <span>{t('share.onlyModeratorsLinks')}</span>
              </div>
            )}

            {canManage && links.isLoading && (
              <div className="py-2 text-left text-sm text-muted-foreground">{t('share.loadingLinks')}</div>
            )}

            {canManage && accessLinks.length > 0 && (
              <div className="space-y-2">
                {accessLinks.map(link => (
                  <LinkItem
                    key={link.id}
                    link={link}
                    diagramId={diagramId}
                    isOrgChartDiagram={isOrgChartDiagram}
                    diagramType={diagramInfo?.type}
                    isPublishableDiagram={isPublishableDiagram}
                    viewerDomain={viewerDomain}
                    viewerRoutePrefix={viewerRoutePrefix}
                    authenticatedFetch={authenticatedFetch}
                    buildFieldVisibilityBody={buildFieldVisibilityBody}
                    onDelete={() => deleteLink.mutate(link.id)}
                    doc={diagramDoc}
                    diagramTitle={diagramInfo?.name}
                  />
                ))}
              </div>
            )}

            {/* Link viewers are anonymous-by-token and therefore public-only. */}
            {hasPublishedFieldVisibility && (
                  <div className="space-y-3 rounded-lg border border-border/60 p-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                      <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>{t('share.publishedFieldVisibility', { defaultValue: 'Published Field Visibility' })}</span>
                    </div>
                    <p className="text-xs text-muted-foreground" data-testid="tid.dialog.share.publicViewerNotice">
                      {t('share.publicViewerFieldsOnly', {
                        defaultValue: 'Viewer links publish public fields only. Internal and restricted data requires authenticated folder access.',
                      })}
                    </p>
                  </div>
            )}
            <div ref={accessLinksBottomRef} aria-hidden="true" className="h-px" />
          </div>

          </div>

          {/* <DialogFooter className="border-t border-border/70 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg h-[40px] px-6">
              Close
            </Button>
          </DialogFooter> */}
        </div>
      </DialogContent>
      </TooltipProvider>
    </Dialog>
  )
}

const CreateLinkRow: React.FC<{
  existingRoles?: Role[]
  onCreate: (role: Role, scope: string) => void
  canManage?: boolean
  entitlementsLoading?: boolean
  isCreating?: boolean
}> = ({ onCreate, existingRoles = [], canManage = true, entitlementsLoading = false, isCreating = false }) => {
  const { t } = useTranslation('diagram')
  const [role, setRole] = React.useState<Role>('viewer')
  const scope = 'diagram:read'

  const availableRoles: Role[] = ['viewer', 'editor', 'moderator']
  const allLinksCreated = availableRoles.every(r => existingRoles.includes(r))

  React.useEffect(() => {
    if (existingRoles.includes(role)) {
      const firstAvailable = availableRoles.find(r => !existingRoles.includes(r))
      if (firstAvailable) setRole(firstAvailable)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingRoles])

  return (
    <div className="flex items-center justify-between gap-4 rounded-lg bg-muted/30 p-3">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <Link className="h-4 w-4 text-primary" />
        </div>
        <p className="truncate text-sm font-medium">{t('share.anyoneWithLink')}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Select
          value={role}
          onValueChange={(value: Role) => setRole(value)}
          disabled={!canManage || entitlementsLoading || allLinksCreated}
        >
          <SelectTrigger data-testid="tid.dialog.share.linkRole" className="w-32 rounded-lg border border-border/60 !h-10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem data-testid="tid.dialog.share.linkRole.viewer" value="viewer" disabled={existingRoles.includes('viewer')}>{t('share.viewer')}</SelectItem>
            <SelectItem data-testid="tid.dialog.share.linkRole.editor" value="editor" disabled={existingRoles.includes('editor')}>{t('share.editor')}</SelectItem>
            <SelectItem data-testid="tid.dialog.share.linkRole.moderator" value="moderator" disabled={existingRoles.includes('moderator')}>{t('share.moderator')}</SelectItem>
          </SelectContent>
        </Select>

        <Button
          data-testid="tid.dialog.share.createLink"
          onClick={() => onCreate(role, scope)}
          disabled={!canManage || entitlementsLoading || isCreating || allLinksCreated || existingRoles.includes(role)}
          className="h-[40px] shrink-0 rounded-lg px-5"
        >
          {isCreating ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Link className="mr-2 h-4 w-4" />
          )}
          {t('share.createLink')}
        </Button>
      </div>
    </div>
  )
}

function formatPublishedTimeAgo(isoDate: string): string {
  const t = i18n.getFixedT(null, 'diagram')
  const seconds = Math.floor((Date.now() - new Date(isoDate).getTime()) / 1000)
  if (!Number.isFinite(seconds) || seconds < 0) {
    return t('share.timeAgoJustNow')
  }
  if (seconds < 60) {
    return t('share.timeAgoJustNow')
  }
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) {
    return t('share.timeAgoMinutes', { count: minutes })
  }
  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    return t('share.timeAgoHours', { count: hours })
  }
  const days = Math.floor(hours / 24)
  return t('share.timeAgoDays', { count: days })
}

const LinkItem: React.FC<{
  link: DiagramAccessLink
  diagramId?: string
  isOrgChartDiagram?: boolean
  diagramType?: string
  isPublishableDiagram?: boolean
  viewerDomain?: string | null
  viewerRoutePrefix?: string | null
  authenticatedFetch: ReturnType<typeof useAuthenticatedFetch>
  buildFieldVisibilityBody: () => FieldVisibilityConfig
  onDelete: () => void
  doc?: import('yjs').Doc | null
  diagramTitle?: string
}> = ({ link, diagramId, isOrgChartDiagram = false, diagramType, isPublishableDiagram = false, viewerDomain, viewerRoutePrefix, authenticatedFetch, buildFieldVisibilityBody, onDelete, doc, diagramTitle }) => {
  const { t } = useTranslation('diagram')
  const [copied, setCopied] = React.useState(false)
  const [linkCopied, setLinkCopied] = React.useState(false)
  const [embedCopied, setEmbedCopied] = React.useState(false)
  const [publishing, setPublishing] = React.useState(false)
  const [updated, setUpdated] = React.useState(false)
  const fullUrl = React.useMemo(() => {
    const url = new URL(window.location.href)
    url.search = ''
    url.searchParams.set('lt', link.token)
    return url.toString()
  }, [link.token])
  const effectiveViewerDomain = viewerDomain ?? (diagramType === 'genogram' ? 'genogram' : 'orgchart')
  const viewerUrl = viewerRoutePrefix
    ? `${window.location.origin}/${viewerRoutePrefix}/${encodeURIComponent(link.token)}`
    : `${window.location.origin}/ov/${encodeURIComponent(link.token)}`
  const isViewerLink = (!!viewerDomain || isPublishableDiagram) && link.role === 'viewer'

  const { data: snapshotStatus, refetch: refetchStatus } = useQuery({
    queryKey: ['viewer-snapshot-status', FOLDER_RECORD_PROJECTION_POLICY_VERSION, link.token],
    queryFn: async () => {
      const res = await authenticatedFetch(`/api/link/viewer/${effectiveViewerDomain}/${encodeURIComponent(link.token)}/snapshot?t=${Date.now()}`)
      if (res.status === 404) return null
      if (!res.ok) throw new Error('Failed to check snapshot status')
      const data = await res.json()
      return {
        publishedAt: data.publishedAt as string,
      }
    },
    staleTime: 60_000,
    enabled: isViewerLink,
  })
  const isPublished = !!snapshotStatus

  const getLinkLabel = (role: Role) => {
    switch (role) {
      case 'viewer': return t('share.viewerAccessLink') || 'Viewer Access Link'
      case 'editor': return t('share.editorAccessLink') || 'Editor Access Link'
      case 'moderator': return t('share.moderatorAccessLink') || 'Moderator Access Link'
      default: return t('share.accessLink') || 'Access Link'
    }
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      Tracker.track(RETENTION.SHARE_COMPLETED, {
        method: 'link',
        role: link.role,
      })
    } catch (error) {
      logger.error('Failed to copy link', error)
    }
  }

  const doPublish = async () => {
    if (!diagramId) return
    const fieldVisibility = buildFieldVisibilityBody()
    const isGenogram = diagramType === 'genogram'

    if (doc && (isOrgChartDiagram || isGenogram)) {
      // Client-side publish: prefer YDoc shapes; fall back to the rendered
      // canvas store for Atlas live charts whose canonical nodes never enter Yjs.
      let payload: ReturnType<typeof buildOrgChartViewerPayload> | ReturnType<typeof buildGenogramViewerPayload>
      if (isGenogram) {
        payload = buildGenogramViewerPayload({ doc, diagramTitle })
        const sceneNodes = Array.isArray(payload.scene?.nodes) ? payload.scene.nodes : payload.nodes
        if (!Array.isArray(sceneNodes) || sceneNodes.length === 0) {
          throw new EmptyViewerSnapshotError()
        }
      } else {
        const storeState = useDiagramStore.getState()
        payload = buildOrgChartViewerPayload({
          doc,
          diagramTitle,
          fieldVisibility,
          canvasScene: {
            nodes: storeState.nodes,
            edges: storeState.edges,
          },
        })
        assertNonEmptyOrgViewerPayload(payload)
      }
      const publishRes = await authenticatedFetch(`/api/viewer/${effectiveViewerDomain}/${encodeURIComponent(link.token)}/publish`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ...payload, diagramId }),
      })
      if (!publishRes.ok) {
        throw new Error(await readPublishErrorMessage(publishRes, 'Failed to publish viewer snapshot'))
      }
    } else {
      // Server-side publish (all diagram types — handles node filtering and domain mapping)
      const publishRes = await authenticatedFetch(`/api/viewer/${effectiveViewerDomain}/${encodeURIComponent(link.token)}/publish-from-diagram`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ diagramId, fieldVisibility }),
      })
      if (!publishRes.ok) {
        throw new Error(await readPublishErrorMessage(publishRes, 'Failed to publish viewer snapshot'))
      }
    }
  }

  const handlePublish = async () => {
    try {
      setPublishing(true)
      await doPublish()
      await refetchStatus()
      await navigator.clipboard.writeText(viewerUrl)
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
    } catch (error) {
      logger.error('Failed to publish viewer', error)
      const message = error instanceof Error ? error.message : 'Failed to publish viewer snapshot'
      toast.error(message)
    } finally {
      setPublishing(false)
    }
  }

  const handleUpdate = async () => {
    try {
      setPublishing(true)
      await doPublish()
      await refetchStatus()
      setUpdated(true)
      setTimeout(() => setUpdated(false), 2000)
    } catch (error) {
      logger.error('Failed to update viewer', error)
      const message = error instanceof Error ? error.message : 'Failed to update viewer snapshot'
      toast.error(message)
    } finally {
      setPublishing(false)
    }
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(viewerUrl)
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
    } catch (error) {
      logger.error('Failed to copy org viewer link', error)
    }
  }

  const handleCopyEmbed = async () => {
    try {
      const embedCode = `<iframe src="${viewerUrl}?embed=1" width="100%" height="720" style="border:0;" loading="lazy" allowfullscreen></iframe>`
      await navigator.clipboard.writeText(embedCode)
      setEmbedCopied(true)
      setTimeout(() => setEmbedCopied(false), 2000)
    } catch (error) {
      logger.error('Failed to copy embed code', error)
    }
  }

  return (
    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
          <Link className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                data-testid={`tid.dialog.share.link.${link.role}.copy`}
                onClick={handleCopy}
                className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
              >
                {copied ? (
                  <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                    <Check className="h-3 w-3" />
                    {t('share.copied') || 'Copied!'}
                  </span>
                ) : (
                  getLinkLabel(link.role)
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent sideOffset={8}>
              {t('share.copyLinkTooltip')}
            </TooltipContent>
          </Tooltip>
          {isViewerLink && (
            <p className="mt-1 max-w-sm text-xs text-muted-foreground">
              {t('share.viewerSnapshotExplanation', { defaultValue: 'Publishes a view-only copy for this link. Publish again to include later changes.' })}
            </p>
          )}
          {isViewerLink && isPublished && snapshotStatus && (
            <div className="text-xs text-muted-foreground mt-0.5">
              {t('share.publishedAt', {
                time: formatPublishedTimeAgo(snapshotStatus.publishedAt),
                defaultValue: `Published ${formatPublishedTimeAgo(snapshotStatus.publishedAt)}`,
              })}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {isViewerLink && !isPublished && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                data-testid={`tid.dialog.share.link.${link.role}.publish`}
                onClick={handlePublish}
                disabled={publishing}
                className="text-blue-600 dark:text-blue-400"
              >
                {linkCopied ? (
                  <span className="flex items-center gap-1">
                    <Check className="h-3 w-3" />
                    {t('share.copied') || 'Copied!'}
                  </span>
                ) : (
                  publishing
                    ? (t('share.publishing', { defaultValue: 'Publishing…' }) as string)
                    : (t('share.publish', { defaultValue: 'Publish' }) as string)
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent sideOffset={8}>
              {t('share.publishViewerSnapshotTooltip', { defaultValue: 'Make the current view available through this viewer link' })}
            </TooltipContent>
          </Tooltip>
        )}
        {isViewerLink && isPublished && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid={`tid.dialog.share.link.${link.role}.copyViewerLink`}
                  onClick={handleCopyLink}
                  className="text-blue-600 dark:text-blue-400"
                >
                  {linkCopied ? (
                    <span className="flex items-center gap-1">
                      <Check className="h-3 w-3" />
                      {t('share.copied') || 'Copied!'}
                    </span>
                  ) : (
                    t('share.copyLink', { defaultValue: 'Copy Link' })
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent sideOffset={8}>
                {t('share.copyOrgViewerLinkTooltip', { defaultValue: 'Copy org viewer link' })}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid={`tid.dialog.share.link.${link.role}.embed`}
                  onClick={handleCopyEmbed}
                  className="text-blue-600 dark:text-blue-400"
                >
                  {embedCopied ? (
                    <span className="flex items-center gap-1">
                      <Check className="h-3 w-3" />
                      {t('share.copied') || 'Copied!'}
                    </span>
                  ) : (
                    t('share.embed', { defaultValue: 'Embed' })
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent sideOffset={8}>
                {t('share.copyIframeEmbedTooltip', { defaultValue: 'Copy iframe embed code' })}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid={`tid.dialog.share.link.${link.role}.update`}
                  onClick={handleUpdate}
                  disabled={publishing}
                >
                  {updated ? (
                    <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                  ) : (
                    <RefreshCw className={`h-4 w-4${publishing ? ' animate-spin' : ''}`} />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent sideOffset={8}>
                {t('share.updatePublishedSnapshotTooltip')}
              </TooltipContent>
            </Tooltip>
          </>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="destructive"
              size="sm"
              data-testid={`tid.dialog.share.link.${link.role}.delete`}
              onClick={onDelete}
              className="hover:bg-red-400 hover:text-white"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent sideOffset={8}>
            {t('share.deleteLinkTooltip')}
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  )
}
